import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';
import HelloModal from 'common/components/hello-modal';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import DocumentPreview from 'hellospa/components/signer-signature-document/preview/document-preview';
import { PreviewSignersDropdown } from 'hellospa/components/signer-signature-document/preview/signers-dropdown';
import { Recipient, RecipientTypes } from 'hellospa/page/prep-and-send/data/types';
import { Signer } from 'hellospa/components/editor/types';
import { UnreachableError } from 'hellospa/ts-utils';
import styles from './preview-modal.scss';
import NotificationList from 'hellospa/components/notification-banner';

type Props = {
  isOpen: boolean,
  close: () => void,
};

const messages = defineMessages({
  title: {
    id: '',
    description: 'Title for preview modal on review page that lets you preview the document as different signers',
    defaultMessage: 'Signer preview',
  },
});

function convertRecipientToEditorSigner(recipient: Recipient, order: number): Signer {
  switch (recipient.type) {
    case RecipientTypes.Signer:
      return {
        id: recipient.id,
        name: recipient.name,
        email: recipient.email,
        order,
      };
    case RecipientTypes.Role:
      return {
        id: recipient.id,
        name: recipient.name,
        email: null,
        order,
      };
    case RecipientTypes.Fax:
      return {
        id: recipient.id,
        name: '',
        email: recipient.fax,
        order,
      };
    default:
      throw new UnreachableError(recipient);
  }
}

export default function PreviewModal({ isOpen, close }: Props) {
  const intl = useIntl();
  const recipients = useSelector(selectors.getRecipients);
  const recipientOrderEnabled = useSelector(selectors.isRecipientOrderEnabled);
  const editorFields = useSelector(selectors.getEditorFields);
  const templateDateFormat = useSelector(selectors.getTemplateDateFormat);
  const signers = React.useMemo(
    () => recipients.map(convertRecipientToEditorSigner),
    [recipients],
  );

  const modalPreviewHeader = (
    <div>
      <div className={styles.modalTitle}>
        {intl.formatMessage(messages.title)}
      </div>
      <PreviewSignersDropdown className={styles.dropdown} />
    </div>
  );
  return (
    <HelloModal
      withFlag={false}
      isCompact={false}
      contentLabel={modalPreviewHeader}
      scrollable={true}
      extraClasses={styles.previewModal}
      contentClasses={styles.content}
      isOpen={isOpen}
      onRequestClose={close}
    >
      <NotificationList className={styles.notificationList} />
      <DocumentPreview
        pages={editorFields.pages}
        signers={signers}
        signerOrderEnabled={recipientOrderEnabled}
        fields={editorFields.fields}
        rules={editorFields.rules}
        dateFormat={templateDateFormat} />

    </HelloModal>
  );
}
